import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Spinner,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import TableContainer from "components/Common/TableContainer";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  getArtists,
  createArtist,
  updateArtist,
  deleteArtist,
  getAllCountries,
} from "api";
import { handleError } from "utils/errorHandler";
import ImageCropper from "../../../components/Common/ImageCropper";

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const categoryOptions = [
  { value: "DJ", label: "DJ" },
  { value: "Singer", label: "Singer" },
  { value: "Band", label: "Band" },
  { value: "Comedian", label: "Comedian" },
  { value: "Other", label: "Other" },
];

const ArtistList = ({ isDashboard }) => {
  document.title = "Artist Management | EventoX";

  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedCropFile, setSelectedCropFile] = useState(null);
  const [cropField, setCropField] = useState(null);

  const toggle = () => setModal(!modal);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      artistName: selectedItem?.artistName ?? "",
      email: selectedItem?.email ?? "",
      phone: selectedItem?.phone ?? "",
      bio: selectedItem?.bio ?? "",
      profileImage: null,
      coverImage: null,
      category: selectedItem?.category ?? "",
      genre: selectedItem?.genre ?? "",
      website: selectedItem?.website ?? "",
      instagram: selectedItem?.instagram ?? "",
      facebook: selectedItem?.facebook ?? "",
      youtube: selectedItem?.youtube ?? "",
      spotify: selectedItem?.spotify ?? "",
      sound_cloud_link: selectedItem?.sound_cloud_link ?? "",
      address: selectedItem?.address ?? "",
      city: selectedItem?.city ?? "",
      countryId: selectedItem?.countryId ?? "",
      isVerified: selectedItem?.isVerified ?? false,
      status: selectedItem?.status ?? "active",
    },
    validationSchema: Yup.object({
      artistName: Yup.string().required("Artist name is required"),
      email: Yup.string().email("Invalid email").nullable(),
      status: Yup.string().oneOf(["active", "inactive"]).required("Status is required"),
    }),
    onSubmit: async (values) => {
      setSubmitLoading(true);
      try {
        const formData = new FormData();
        formData.append("artistName", values.artistName);
        if (values.email) formData.append("email", values.email);
        if (values.phone) formData.append("phone", values.phone);
        if (values.bio) formData.append("bio", values.bio);
        if (values.category) formData.append("category", values.category);
        if (values.genre) formData.append("genre", values.genre);
        if (values.website) formData.append("website", values.website);
        if (values.instagram) formData.append("instagram", values.instagram);
        if (values.facebook) formData.append("facebook", values.facebook);
        if (values.youtube) formData.append("youtube", values.youtube);
        if (values.spotify) formData.append("spotify", values.spotify);
        if (values.sound_cloud_link) formData.append("sound_cloud_link", values.sound_cloud_link);
        if (values.address) formData.append("address", values.address);
        if (values.city) formData.append("city", values.city);
        if (values.countryId) formData.append("countryId", String(values.countryId));
        formData.append("isVerified", values.isVerified ? "true" : "false");
        formData.append("status", values.status);

        if (values.profileImage instanceof File) {
          formData.append("profileImage", values.profileImage);
        }
        if (values.coverImage instanceof File) {
          formData.append("coverImage", values.coverImage);
        }

        if (isEdit && (selectedItem?.artistId ?? selectedItem?.id)) {
          const id = selectedItem.artistId ?? selectedItem.id;
          await updateArtist(id, formData);
          toast.success("Artist updated successfully");
        } else {
          await createArtist(formData);
          toast.success("Artist created successfully");
        }

        const refreshed = await getArtists();
        setArtists(Array.isArray(refreshed) ? refreshed : []);
        validation.resetForm();
        setSelectedItem(null);
        setIsEdit(false);
        toggle();
      } catch (err) {
        handleError(err);
        toast.error("Error saving artist");
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  useEffect(() => {
    let url = null;
    if (validation.values.profileImage instanceof File) {
      url = URL.createObjectURL(validation.values.profileImage);
      setProfilePreviewUrl(url);
    } else {
      setProfilePreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [validation.values.profileImage]);

  useEffect(() => {
    let url = null;
    if (validation.values.coverImage instanceof File) {
      url = URL.createObjectURL(validation.values.coverImage);
      setCoverPreviewUrl(url);
    } else {
      setCoverPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [validation.values.coverImage]);

  useEffect(() => {
    const load = async () => {
      try {
        const [data, countriesData] = await Promise.all([
          getArtists(),
          getAllCountries().catch(() => []),
        ]);
        setArtists(Array.isArray(data) ? data : []);
        setCountries(Array.isArray(countriesData) ? countriesData : []);
      } catch (err) {
        handleError(err);
        toast.error("Failed to load artists");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onClickDelete = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    const id = selectedItem?.artistId ?? selectedItem?.id;
    if (!id) {
      setDeleteModal(false);
      return;
    }
    try {
      await deleteArtist(id);
      const refreshed = await getArtists();
      setArtists(Array.isArray(refreshed) ? refreshed : []);
      toast.success("Artist deleted");
    } catch (err) {
      handleError(err);
      toast.error("Error deleting artist");
    }
    setDeleteModal(false);
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEdit(true);
    validation.resetForm();
    toggle();
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsEdit(false);
    validation.resetForm();
    toggle();
  };

  const columns = useMemo(
    () => [
      {
        header: "Artist",
        accessorKey: "artistName",
        cell: (cell) => {
          const row = cell.row.original;
          const img = row.profileImage ?? row.profilePic;
          const name = row.artistName || "";
          return (
            <div className="d-flex align-items-center">
              <div className="avatar-xs flex-shrink-0 me-2">
                {img ? (
                  <img
                    src={img}
                    alt={name}
                    className="rounded-circle"
                    style={{ objectFit: "cover", width: 36, height: 36 }}
                  />
                ) : (
                  <span className="avatar-title rounded-circle bg-primary bg-soft text-primary">
                    {name.charAt(0).toUpperCase() || "?"}
                  </span>
                )}
              </div>
              <span className="fw-medium">{name || "—"}</span>
            </div>
          );
        },
      },
      {
        header: "Contact",
        accessorKey: "email",
        cell: (cell) => {
          const row = cell.row.original;
          const email = row.email || "";
          const phone = row.phone ?? row.phoneNo ?? "";
          if (!email && !phone) return "—";
          return (
            <div className="text-break">
              {email && <div>{email}</div>}
              {phone && <div className="text-muted small">{phone}</div>}
            </div>
          );
        },
      },
      { header: "Category", accessorKey: "category" },
      { header: "Genre", accessorKey: "genre" },
      {
        header: "Status",
        accessorKey: "status",
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          if (val === undefined || val === null) return false;
          return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
        },
        cell: (cell) => (
          <span
            className={`badge ${
              cell.getValue() === "active" ? "bg-success" : "bg-secondary"
            }`}
          >
            {cell.getValue() || "—"}
          </span>
        ),
      },
      {
        header: "Verified",
        accessorKey: "isVerified",
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          const display = val ? "yes" : "no";
          return display.includes(String(filterValue).toLowerCase());
        },
        cell: (cell) =>
          cell.getValue() ? (
            <span className="badge bg-success">
              <i className="mdi mdi-check-circle me-1" />
              Yes
            </span>
          ) : (
            <span className="badge bg-secondary">No</span>
          ),
      },
      {
        header: 'Created Date',
        accessorKey: 'createdAt',
        enableColumnFilter: true,
        enableSorting: true,
        filterFn: (row, columnId, filterValue) => {
          const val = row.getValue(columnId);
          if (!val) return false;
          const display = new Date(val).toLocaleDateString();
          return display.toLowerCase().includes(String(filterValue).toLowerCase());
        },
        cell: (cell) => {
          const date = cell.getValue();
          return date ? new Date(date).toLocaleDateString() : "-";
        }
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => handleEdit(item)}>Edit</DropdownItem>
                <DropdownItem onClick={() => onClickDelete(item)}>Delete</DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  const content = (
    <>
      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : (
                <TableContainer
                  columns={columns}
                  data={artists}
                  isGlobalFilter
                  isPagination
                  SearchPlaceholder="Search artists..."
                  isCustomPageSize={true}
                  isAddButton={true}
                  handleUserClick={handleAddNew}
                  buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                  buttonName="Add Artist"
                  tableClass="align-middle table-hover w-100"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate pagination-rounded"
                  pagination="pagination"
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modal} toggle={toggle} size="lg" scrollable>
        <ModalHeader toggle={toggle}>
          {isEdit ? "Edit Artist" : "Add Artist"}
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label>Artist Name *</Label>
              <Input
                name="artistName"
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.artistName}
                invalid={
                  validation.touched.artistName && validation.errors.artistName
                }
              />
              <FormFeedback>{validation.errors.artistName}</FormFeedback>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Email</Label>
                  <Input
                    name="email"
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email}
                    invalid={
                      validation.touched.email && validation.errors.email
                    }
                  />
                  <FormFeedback>{validation.errors.email}</FormFeedback>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Phone</Label>
                  <Input
                    name="phone"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.phone}
                  />
                </div>
              </div>
            </div>

            <div className="mb-3">
              <Label>Bio</Label>
              <Input
                name="bio"
                type="textarea"
                rows={2}
                onChange={validation.handleChange}
                value={validation.values.bio}
              />
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Category</Label>
                  <Input
                    name="category"
                    type="select"
                    onChange={validation.handleChange}
                    value={validation.values.category}
                  >
                    <option value="">Select</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Genre</Label>
                  <Input
                    name="genre"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.genre}
                    placeholder="e.g. EDM, Rock"
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Profile Image</Label>
                  <Input
                    name="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedCropFile(file);
                        setCropField("profileImage");
                        setIsCropperOpen(true);
                      }
                      e.target.value = '';
                    }}
                  />
                  {(profilePreviewUrl && (
                    <img
                      src={profilePreviewUrl}
                      alt="Profile preview"
                      className="mt-2 rounded"
                      style={{ maxHeight: 80, maxWidth: "100%" }}
                    />
                  )) ||
                    (isEdit && selectedItem?.profileImage && (
                      <p className="mt-2 small text-muted mb-0">
                        Current: <a href={selectedItem.profileImage} target="_blank" rel="noopener noreferrer">View</a>
                      </p>
                    ))}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Cover Image</Label>
                  <Input
                    name="coverImage"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      validation.setFieldValue("coverImage", file || null);
                    }}
                  />
                  {(coverPreviewUrl && (
                    <img
                      src={coverPreviewUrl}
                      alt="Cover preview"
                      className="mt-2 rounded"
                      style={{ maxHeight: 80, maxWidth: "100%" }}
                    />
                  )) ||
                    (isEdit && selectedItem?.coverImage && (
                      <p className="mt-2 small text-muted mb-0">
                        Current: <a href={selectedItem.coverImage} target="_blank" rel="noopener noreferrer">View</a>
                      </p>
                    ))}
                </div>
              </div>
            </div>

            <h6 className="mb-2">Social &amp; Links</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Website</Label>
                  <Input
                    name="website"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.website}
                    placeholder="https://"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Instagram</Label>
                  <Input
                    name="instagram"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.instagram}
                    placeholder="https://instagram.com/..."
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Facebook</Label>
                  <Input
                    name="facebook"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.facebook}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>YouTube</Label>
                  <Input
                    name="youtube"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.youtube}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Spotify</Label>
                  <Input
                    name="spotify"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.spotify}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>SoundCloud</Label>
                  <Input
                    name="sound_cloud_link"
                    type="url"
                    onChange={validation.handleChange}
                    value={validation.values.sound_cloud_link}
                    placeholder="https://soundcloud.com/..."
                  />
                </div>
              </div>
            </div>

            <h6 className="mb-2">Location</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Address</Label>
                  <Input
                    name="address"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.address}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>City</Label>
                  <Input
                    name="city"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.city}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Country</Label>
                  <Input
                    name="countryId"
                    type="select"
                    onChange={validation.handleChange}
                    value={validation.values.countryId}
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option
                        key={c.countryId ?? c.id}
                        value={c.countryId ?? c.id}
                      >
                        {c.countryName ?? c.name ?? c.country ?? ""}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <Label>Status *</Label>
                  <Input
                    name="status"
                    type="select"
                    onChange={validation.handleChange}
                    value={validation.values.status}
                    invalid={
                      validation.touched.status && validation.errors.status
                    }
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{validation.errors.status}</FormFeedback>
                </div>
              </div>
              <div className="col-md-6">
                <FormGroup check className="mb-3 mt-4">
                  <Input
                    name="isVerified"
                    type="checkbox"
                    checked={validation.values.isVerified}
                    onChange={validation.handleChange}
                  />
                  <Label check>Verified</Label>
                </FormGroup>
              </div>
            </div>

            <div className="text-end">
              <Button
                color="secondary"
                type="button"
                className="me-2"
                onClick={toggle}
              >
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={submitLoading}>
                {submitLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={isCropperOpen} toggle={() => setIsCropperOpen(false)} size="lg" style={{ zIndex: 1060 }}>
        <ModalHeader toggle={() => setIsCropperOpen(false)}>Crop Image</ModalHeader>
        <ModalBody>
          {selectedCropFile && (
            <ImageCropper
              imageFile={selectedCropFile}
              onCropComplete={(croppedFile) => {
                validation.setFieldValue(cropField, croppedFile);
                setIsCropperOpen(false);
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );

  if (isDashboard) {
    return (
      <React.Fragment>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        {content}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Artist Management" />
          {content}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ArtistList);
